import React, { useState, useRef, forwardRef } from 'react';
import ConstructionTab from './ConstructionTab';
import OperationTab from './OperationTab';
import SummaryTab from './SummaryTab';
import {
  BuildingConstruction,
  LandUseChange,
  RoadPavement,
} from '../../../../../../services/types/Calculator';
import calculatorTransportPavmentsService from '../../../../../../services/calculatorTransportPavmentsService';
import {
  CalculatorTransportDto,
  EmissionsPurchasedElectricity,
  EmissionsRoadMaintenanceActivities,
} from '../../../../../../services/types/CalculatorTransportRoads';
import SectorTabContainerBase, {
  SectorTabContainerBaseProps,
  TabContentBase,
  SectorTabContainerBaseHandlers,
  SectorTabContainerBaseHandle,
} from '../Shared/SectorTabContainerBase';

interface Props extends SectorTabContainerBaseProps {
  landUseChange: LandUseChange;
  roadPavementTransport: RoadPavement;
  emissionsDevelopmentBuildingConstruction: BuildingConstruction;
  emissionsRoadMaintenanceActivities: EmissionsRoadMaintenanceActivities;
  emissionsPurchasedElectricity: EmissionsPurchasedElectricity;
}

const SectorTabContainer = forwardRef<SectorTabContainerBaseHandle, Props>(
  (
    {
      // construction
      landUseChange: initialLandUseChangeProp,
      roadPavementTransport: initialRoadPavementTransportProp,
      emissionsDevelopmentBuildingConstruction: initialEmissionsDevelopmentBuildingConstructionProp,
      // operation
      emissionsRoadMaintenanceActivities: initialEmissionsRoadMaintenanceActivitiesProp,
      emissionsPurchasedElectricity: initialEmissionsPurchasedElectricityProp,
      ...baseProps
    }: Props,
    ref
  ) => {
    // construction
    const [landUseChange, setLandUseChange] = useState<LandUseChange>(initialLandUseChangeProp);
    const [roadPavementTransport, setRoadPavementTransport] = useState<RoadPavement>(
      initialRoadPavementTransportProp
    );
    const [emissionsDevelopmentBuildingConstruction, setEmissionsDevelopmentBuildingConstruction] =
      useState<BuildingConstruction>(initialEmissionsDevelopmentBuildingConstructionProp);

    // operations
    const [emissionsRoadMaintenanceActivities, setEmissionsRoadMaintenanceActivities] =
      useState<EmissionsRoadMaintenanceActivities>(initialEmissionsRoadMaintenanceActivitiesProp);

    const [emissionsPurchasedElectricity, setEmissionsPurchasedElectricity] =
      useState<EmissionsPurchasedElectricity>(initialEmissionsPurchasedElectricityProp);

    // Ver el comentario largo en UrbanInfrastructureTool/SectorTabContainer.tsx:
    // updateInitialValues() necesita persistir entre renders (useRef), no
    // reasignar las variables desestructuradas de los props (se pierden en
    // el siguiente render) - mismo bug real, misma causa, mismo fix.
    const initialLandUseChangeRef = useRef(initialLandUseChangeProp);
    const initialRoadPavementTransportRef = useRef(initialRoadPavementTransportProp);
    const initialEmissionsDevelopmentBuildingConstructionRef = useRef(
      initialEmissionsDevelopmentBuildingConstructionProp
    );
    const initialEmissionsRoadMaintenanceActivitiesRef = useRef(
      initialEmissionsRoadMaintenanceActivitiesProp
    );
    const initialEmissionsPurchasedElectricityRef = useRef(
      initialEmissionsPurchasedElectricityProp
    );

    // Función para construir el DTO común
    const buildCalculatorTransportDto = (): CalculatorTransportDto => ({
      CalculatorId: baseProps.calculatorId,
      // construction
      LandUseChangeRows: landUseChange.rows,
      LandUseTotal: landUseChange.subTotal,
      RoadPavementTransport: roadPavementTransport,
      RoadPavementTransportTotal: roadPavementTransport.subTotal,
      EmissionsDevelopmentBuildingConstruction: emissionsDevelopmentBuildingConstruction,
      EmissionsDevelopmentBuildingConstructionTotal:
        emissionsDevelopmentBuildingConstruction.subTotal,
      // operations
      EmissionsRoadMaintenanceActivities: emissionsRoadMaintenanceActivities,
      EmissionsRoadMaintenanceActivitiesTotal: emissionsRoadMaintenanceActivities.subTotal,
      EmissionsPurchasedElectricity: emissionsPurchasedElectricity,
      EmissionsPurchasedElectricityTotal: emissionsPurchasedElectricity.subTotal,
      // totals
      TotalConstructionOverriden: baseProps.totalConstructionOverriden,
      TotalOperationOverriden: baseProps.totalOperationOverriden,
    });

    const handlers: SectorTabContainerBaseHandlers = {
      buildDto: buildCalculatorTransportDto,
      saveChanges: async () => {
        const calculatorTransportDto = buildCalculatorTransportDto();
        return calculatorTransportPavmentsService.saveCalculatorTransport(calculatorTransportDto);
      },
      finishCalculator: async () => {
        const calculatorTransportDto = buildCalculatorTransportDto();
        return calculatorTransportPavmentsService.finishCalculatorTransport(calculatorTransportDto);
      },
      updateInitialValues: () => {
        initialLandUseChangeRef.current = { ...landUseChange };
        initialRoadPavementTransportRef.current = { ...roadPavementTransport };
        initialEmissionsDevelopmentBuildingConstructionRef.current = {
          ...emissionsDevelopmentBuildingConstruction,
        };
        initialEmissionsRoadMaintenanceActivitiesRef.current = {
          ...emissionsRoadMaintenanceActivities,
        };
        initialEmissionsPurchasedElectricityRef.current = { ...emissionsPurchasedElectricity };
      },
      hasChanges: () =>
        JSON.stringify(landUseChange) !== JSON.stringify(initialLandUseChangeRef.current) ||
        JSON.stringify(roadPavementTransport) !==
          JSON.stringify(initialRoadPavementTransportRef.current) ||
        JSON.stringify(emissionsDevelopmentBuildingConstruction) !==
          JSON.stringify(initialEmissionsDevelopmentBuildingConstructionRef.current) ||
        JSON.stringify(emissionsRoadMaintenanceActivities) !==
          JSON.stringify(initialEmissionsRoadMaintenanceActivitiesRef.current) ||
        JSON.stringify(emissionsPurchasedElectricity) !==
          JSON.stringify(initialEmissionsPurchasedElectricityRef.current),
      checkForNullValues: () =>
        landUseChange.subTotal === null &&
        roadPavementTransport.subTotal === null &&
        emissionsDevelopmentBuildingConstruction.subTotal === null &&
        emissionsRoadMaintenanceActivities.subTotal === null &&
        emissionsPurchasedElectricity.subTotal === null &&
        baseProps.totalConstructionOverriden === null &&
        baseProps.totalOperationOverriden === null,
    };

    const tabContent: TabContentBase = {
      constructionTab: (
        <ConstructionTab
          landUseChange={landUseChange}
          setLandUseChange={setLandUseChange}
          roadPavementTransport={roadPavementTransport}
          setRoadPavementTransport={setRoadPavementTransport}
          emissionsDevelopmentBuildingConstruction={emissionsDevelopmentBuildingConstruction}
          setEmissionsDevelopmentBuildingConstruction={setEmissionsDevelopmentBuildingConstruction}
        />
      ),
      operationTab: (
        <OperationTab
          emissionsRoadMaintenanceActivities={emissionsRoadMaintenanceActivities}
          setEmissionsRoadMaintenanceActivities={setEmissionsRoadMaintenanceActivities}
          emissionsPurchasedElectricity={emissionsPurchasedElectricity}
          setEmissionsPurchasedElectricity={setEmissionsPurchasedElectricity}
        />
      ),
      summaryTab: (
        <SummaryTab
          landUseChangeTotal={landUseChange.subTotal}
          roadPavementTransportTotal={roadPavementTransport.subTotal}
          emissionsDevelopmentBuildingConstructionTotal={
            emissionsDevelopmentBuildingConstruction.subTotal
          }
          roadMaintenanceTotal={emissionsRoadMaintenanceActivities.subTotal}
          emissionsPurchasedElectricityTotal={emissionsPurchasedElectricity.subTotal}
          totalConstructionOverriden={baseProps.totalConstructionOverriden}
          setTotalConstructionOverriden={baseProps.setTotalConstructionOverriden}
          totalOperationOverriden={baseProps.totalOperationOverriden}
          setTotalOperationOverriden={baseProps.setTotalOperationOverriden}
        />
      ),
    };

    return (
      <SectorTabContainerBase
        {...baseProps}
        tabContent={tabContent}
        handlers={handlers}
        ref={ref}
      />
    );
  }
);

export default SectorTabContainer;
